import * as React from "react";
import styled from "styled-components";

import {
  CheckboxDescription,
  FieldSetToggleDisplay,
  FooterNote,
  SettingsFormDiv,
  SettingsInputItem,
} from "../../../../App/Common/Components/FormComponents";
import { ILocalizedMessageApi } from "../../../../reducers/course-author/types";
import { fullCourseViews } from "../../../../utils/courseViews";
import { PreferredLocale } from "../../../../utils/locales";
import { HTMLEditor } from "../../../course-author/html-editor/Editor.container";
import { HTMLRenderer } from "../../../course-author/html-editor/Renderer";
import { ICourseEditOperations, ICourseSettings, IFullCourse } from "../../../course-author/types";
import Checkbox from "../Checkbox";
import LocalizedMessage from "./LocalizedMessage";

const Note = styled.div`
  margin-bottom: 2rem;
`;

export interface IEditCourseCompletionMessageProps {
  course: IFullCourse;
  currentLocale: PreferredLocale;
  editOperations: ICourseEditOperations;
  localizedMessageApi: ILocalizedMessageApi;
}

export interface IEditCourseCompletionMessageState {
  edit: boolean;
  message: string;
}

export default class EditCourseCompletionMessage extends React.Component<
  IEditCourseCompletionMessageProps,
  IEditCourseCompletionMessageState
> {
  public containerRef = React.createRef<HTMLDivElement>();
  constructor(props: IEditCourseCompletionMessageProps) {
    super(props);
    this.state = {
      edit: false,
      message: this.getCustomCompletedMessage(),
    };
  }

  public componentDidUpdate(oldProps: IEditCourseCompletionMessageProps) {
    if (this.props.currentLocale !== oldProps.currentLocale) {
      this.setState({ message: this.getCustomCompletedMessage(), edit: false });
    }
  }

  public toggleEdit = () => this.setState({ edit: !this.state.edit });

  public toggleUseCustomCompledtedMessage = () => {
    this.props.editOperations.modifyUseCustomCompletedMessage();
  };

  public getCustomCompletedMessage = () =>
    fullCourseViews.customCompletedMessage(this.props.course, this.props.currentLocale) || "";

  public getMarkdown = () => this.state.message;

  public setCustomCompletedMessage = (message: string) => {
    if (message !== this.getCustomCompletedMessage()) {
      this.props.editOperations.modifyCustomCompletedMessage(message);
      this.setState({ message });
    }
    this.toggleEdit();
  };

  public render() {
    const {
      localizedMessageApi: { getExitCourseExplanation, getExitCourseScormExplanation },
    } = this.props;
    const settings = fullCourseViews.settings(this.props.course) as ICourseSettings;
    return (
      <SettingsFormDiv>
        <SettingsInputItem>
          <label>
            <Checkbox
              defaultChecked={settings.useCustomCompletedMessage}
              onChange={this.toggleUseCustomCompledtedMessage}
            />
            <CheckboxDescription>Use custom text</CheckboxDescription>
          </label>
        </SettingsInputItem>
        <Note>
          <FooterNote>
            You can customize the text of the recommendation which the user sees when they have completed the course.
          </FooterNote>
        </Note>
        {settings.useCustomCompletedMessage ? (
          <div ref={this.containerRef}>
            {!this.state.edit && (
              <FieldSetToggleDisplay
                legend={"Custom Course Completion Message:"}
                onClick={this.toggleEdit}
                shouldDisplay={!this.state.edit}
              >
                <HTMLRenderer source={this.state.message} placeholder={"Type here"} />
              </FieldSetToggleDisplay>
            )}
            {this.state.edit && (
              <HTMLEditor
                getContent={this.getMarkdown}
                setContent={this.setCustomCompletedMessage}
                currentLocale={this.props.currentLocale}
                shouldAutoFocus={false}
                containerRef={this.containerRef}
                toggleEdit={this.toggleEdit}
              />
            )}
          </div>
        ) : (
          <>
            <LocalizedMessage
              locale={this.props.currentLocale}
              getMessage={getExitCourseExplanation}
              title="Default normal-mode message: "
            />
            <LocalizedMessage
              locale={this.props.currentLocale}
              getMessage={getExitCourseScormExplanation}
              title="Default SCORM-mode message: "
            />
          </>
        )}
      </SettingsFormDiv>
    );
  }
}
